import { ActivityImpactKind, ActivityType, SystemActivity } from '../../../models/home';
import { HomeActivityEventResponse } from '../../../models/home/dtos';

export class ActivityAdapter {
  static toViewModel(dto: HomeActivityEventResponse): SystemActivity {
    const eventType = dto.eventType ?? "";
    const documentLabel = ActivityAdapter.formatDocumentLabel(dto.documentTitle ?? dto.documentId);
    const impactLabel = ActivityAdapter.formatImpact(eventType, dto.impactDisplayName ?? dto.impactSummary);
    const description = ActivityAdapter.pickDescription(dto, documentLabel, impactLabel);

    return {
      type: ActivityAdapter.mapType(dto.eventType),
      description,
      timestamp: dto.occurredAt ?? new Date().toISOString(),
      documentsAffected: dto.impactScore ?? 0,
      documentsExamples: documentLabel ? [documentLabel] : [],
      impact: {
        kind: ActivityAdapter.mapImpactKind(dto.eventType),
        summary: impactLabel,
        affectedDocuments: documentLabel ? [documentLabel] : [],
        impactedQueueItems: [],
        relatedRole: undefined,
      },
    };
  }

  private static mapType(eventType?: string | null): ActivityType {
    const normalized = (eventType ?? '').toLowerCase();
    if (normalized.includes('ingest')) return 'ingestion';
    if (normalized.includes('extract')) return 'extraction';
    if (normalized.includes('normal')) return 'normalization';
    if (normalized.includes('valid')) return 'validation';
    return 'classification';
  }

  private static mapImpactKind(eventType?: string | null): ActivityImpactKind {
    const normalized = (eventType ?? '').toLowerCase();
    if (normalized.includes('reclass')) return 'reclassification';
    if (normalized.includes('date')) return 'new_date';
    if (normalized.includes('priority')) return 'priority_shift';
    return 'reclassification';
  }

  private static pickDescription(
    dto: HomeActivityEventResponse,
    documentLabel: string,
    impactLabel: string
  ): string {
    if (dto.payloadSummary && !ActivityAdapter.looksLikeJson(dto.payloadSummary)) {
      return dto.payloadSummary;
    }
    if (dto.impactDisplayName && !ActivityAdapter.looksLikeJson(dto.impactDisplayName)) {
      return dto.impactDisplayName;
    }
    if (dto.impactSummary && !ActivityAdapter.looksLikeJson(dto.impactSummary)) {
      return dto.impactSummary;
    }
    if (dto.eventTypeDisplayName) {
      return dto.eventTypeDisplayName;
    }
    if (impactLabel) {
      return impactLabel;
    }
    if (documentLabel) {
      return `Actualización en ${documentLabel}`;
    }
    return "Actividad reciente";
  }

  private static formatDocumentLabel(value?: string | null): string {
    if (!value) return "";
    const trimmed = value.trim();
    if (/^[0-9a-fA-F-]{8,}$/.test(trimmed) && trimmed.includes("-")) {
      return `Documento ${trimmed.slice(0, 8)}`;
    }
    return value;
  }

  private static formatImpact(eventType: string, impactSummary?: string | null): string {
    if (impactSummary && !ActivityAdapter.looksLikeJson(impactSummary)) {
      return impactSummary;
    }
    const normalized = eventType.toLowerCase();
    if (normalized.includes("priority")) {
      return "Prioridad recalibrada por la IA";
    }
    if (normalized.includes("date")) {
      return "Se detectó una fecha crítica";
    }
    if (normalized.includes("ingest")) {
      return "Nuevo documento procesado";
    }
    if (normalized.includes("extract")) {
      return "Extracción de datos completada";
    }
    if (normalized.includes("normal")) {
      return "Normalización finalizada";
    }
    if (normalized.includes("valid")) {
      return "Validación completada";
    }
    return "Clasificación detectada por la IA";
  }

  private static looksLikeJson(value: string): boolean {
    const trimmed = value.trim();
    return trimmed.startsWith("{") || trimmed.startsWith("[");
  }
}
